import React, { useEffect, useMemo, useRef, useState } from "react";
import { studentTestimonialApi } from "../../config/api";
import { apiClient, apiFormDataClient } from "../../utils/apiClient";
import "./StudentTestimonial.scss";

const defaultForm = {
  _id: null,
  Name: "",
  Title: "",
  Review: "",
  Display_Order: 0,
  Photo: ""
};

const StudentTestimonial = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [modalType, setModalType] = useState(""); // "add" | "edit"
  const [formData, setFormData] = useState(defaultForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const response = await apiClient(studentTestimonialApi);
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const result = await response.json();
        if (response.ok) {
          setData(result.data || []);
        } else {
          console.error("Failed to fetch student testimonials:", result);
        }
      } else {
        const text = await response.text();
        console.error("Non-JSON response from student testimonial API:", text);
      }
    } catch (error) {
      console.error("Error fetching student testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const matchName = (item.Name || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchTitle = (item.Title || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchReview = (item.Review || "").toLowerCase().includes(searchQuery.toLowerCase());
      return matchName || matchTitle || matchReview;
    });
  }, [data, searchQuery]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpenId(null);
      }
    };
    const closeDropdown = () => setDropdownOpenId(null);
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", closeDropdown, true);
    window.addEventListener("resize", closeDropdown);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", closeDropdown, true);
      window.removeEventListener("resize", closeDropdown);
    };
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    if (dropdownOpenId === id) {
      setDropdownOpenId(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 160 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => {
    setFormData(defaultForm);
    setSelectedFile(null);
    setPreviewUrl(null);
    setModalType("add");
    setDropdownOpenId(null);
  };

  const openEditModal = (item) => {
    setFormData({ ...item });
    setSelectedFile(null);
    setPreviewUrl(item.Photo || null);
    setModalType("edit");
    setDropdownOpenId(null);
  };

  const closeModal = () => {
    setModalType("");
    setFormData(defaultForm);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData((prev) => ({ ...prev, Photo: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? studentTestimonialApi : `${studentTestimonialApi}/${formData._id}`;

      const dataToSend = new FormData();
      dataToSend.append("Name", formData.Name);
      dataToSend.append("Title", formData.Title);
      dataToSend.append("Review", formData.Review);
      dataToSend.append("Display_Order", formData.Display_Order);

      if (selectedFile) {
        dataToSend.append("Photo", selectedFile);
      } else if (formData.Photo) {
        dataToSend.append("Photo", formData.Photo);
      } else {
        dataToSend.append("Photo", "");
      }

      const response = await apiFormDataClient(url, {
        method,
        body: dataToSend
      });

      if (response.ok) {
        fetchTestimonials();
        closeModal();
      } else {
        let errMsg = "Failed to save student testimonial.";
        try {
          const errResult = await response.json();
          errMsg = errResult.message || errMsg;
        } catch (e) {
          console.error("Non-JSON error response from server:", e);
        }
        alert(errMsg);
      }
    } catch (error) {
      console.error("Error saving student testimonial:", error);
      alert("Error saving student testimonial.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await apiClient(`${studentTestimonialApi}/${id}`, {
        method: "DELETE"
      });
      if (response.ok) {
        fetchTestimonials();
        setConfirmId(null);
      } else {
        const errResult = await response.json();
        alert(errResult.message || "Failed to delete student testimonial.");
      }
    } catch (error) {
      console.error("Error deleting student testimonial:", error);
      alert("Error deleting student testimonial.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="st-page">
      <div className="st-header">
        <div>
          <h1>Student Testimonial</h1>
          <p>Manage student reviews, testimonials, photos, and display preferences shown on the website.</p>
        </div>
        <button type="button" className="st-add-btn" onClick={openAddModal}>
          <span>+</span> Add Testimonial
        </button>
      </div>

      <div className="st-filter-card">
        <div className="st-filter-grid">
          <div className="form-group">
            <label>Search Testimonials</label>
            <input
              type="text"
              placeholder="Search by Student Name, Title, or Review content"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="st-table-card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Photo</th>
                <th>Student Name</th>
                <th>Title / Designation</th>
                <th>Testimonial / Review</th>
                <th>Order</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <div className="table-photo">
                        {item.Photo ? (
                          <img src={item.Photo} alt={item.Name} />
                        ) : (
                          <div className="photo-placeholder">{(item.Name || "?").charAt(0).toUpperCase()}</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="name-cell">
                        <strong>{item.Name}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="title-badge">{item.Title}</span>
                    </td>
                    <td className="review-cell">
                      <div className="review-text" title={item.Review}>
                        "{item.Review}"
                      </div>
                    </td>
                    <td>{item.Display_Order}</td>
                    <td className="actions-cell">
                      <button type="button" className="dots-btn" onClick={(e) => handleDotsClick(e, item._id)}>
                        ⋮
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="empty-cell">
                    No student testimonials found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {dropdownOpenId !== null && (
        <div
          ref={dropdownRef}
          className="action-dropdown"
          style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 99999 }}
        >
          <button type="button" onClick={() => openEditModal(data.find((i) => i._id === dropdownOpenId))}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
            Edit
          </button>
          <button
            type="button"
            className="delete"
            onClick={() => {
              setConfirmId(dropdownOpenId);
              setDropdownOpenId(null);
            }}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6M14 11v6" />
              <path d="M9 6V4h6v2" />
            </svg>
            Delete
          </button>
        </div>
      )}

      {confirmId !== null && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#ef4444" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14H6L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4h6v2" />
              </svg>
            </div>
            <h3>Delete Testimonial?</h3>
            <p>This will remove the student testimonial from the website.</p>
            <div className="confirm-actions">
              <button type="button" className="btn btn-light" onClick={() => setConfirmId(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(confirmId)}>
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="st-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalType === "add" ? "Add Student Testimonial" : "Edit Student Testimonial"}</h2>
              <button type="button" className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            
            <form className="modal-form" onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-section-label">Student Photo</div>
                <div className="form-group">
                  <label>Photo / Avatar</label>
                  <div className="photo-upload-box" onClick={triggerFileInput}>
                    {previewUrl ? (
                      <div className="photo-preview-block">
                        <img src={previewUrl} alt="Preview" className="profile-preview" />
                        <div className="photo-preview-actions">
                          <button type="button" className="photo-action-btn" onClick={handleRemovePhoto}>
                            Remove Photo
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <span>Click to upload photo</span>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>

                <div className="form-section-label">Student Information</div>
                <div className="form-group">
                  <label>Student Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.Name}
                    onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                    placeholder="e.g. Ananya Sharma"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Title / Course / Batch <span className="required">*</span></label>
                  <input
                    type="text"
                    value={formData.Title}
                    onChange={(e) => setFormData({ ...formData, Title: e.target.value })}
                    placeholder="e.g. B.Tech Computer Science, Batch 2024"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Testimonial / Review <span className="required">*</span></label>
                  <textarea
                    rows="4"
                    value={formData.Review}
                    onChange={(e) => setFormData({ ...formData, Review: e.target.value })}
                    placeholder="Enter the detailed review or testimonial provided by the student..."
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Display Order</label>
                  <input
                    type="number"
                    value={formData.Display_Order}
                    onChange={(e) => setFormData({ ...formData, Display_Order: Number(e.target.value) })}
                    placeholder="e.g. 1"
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-light" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Testimonial
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTestimonial;
